import { createStore } from 'zustand/vanilla';

const createUtilityBillStore = (supabaseClient, getUserId) => {
  if (!supabaseClient) {
    throw new Error('Supabase client is required to initialize the utility bill store');
  }

  if (!getUserId || typeof getUserId !== 'function') {
    throw new Error('getUserId function is required to initialize the utility bill store');
  }


  try {
    const store = createStore((set, get) => ({
      utilityBills: [],
      filteredUtilityBills: [],
      isLoading: false,
      error: null,
      isConnected: false,
      filters: {
        dateFrom: null,
        dateTo: null,
        amountMin: null,
        amountMax: null,
        searchTerm: '',
        status: 'all',
      },
      sortBy: 'due_date-desc',

      setConnectionStatus: (status) => set({ isConnected: status }),

      fetchUtilityBills: async () => {
        set({ isLoading: true, error: null });
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { data, error } = await supabaseClient
            .from('utility_bills')
            .select('*, utility_services(*, utility_charges(*))')
            .eq('user_id', userId)
            .order('due_date', { ascending: false });

          if (error) throw error;

          set({
            utilityBills: data || [],
            isLoading: false,
            isConnected: true,
          });
          get().applyFiltersAndSort();
        } catch (error) {
          console.error('Error fetching utility bills:', error.message);
          set({
            error: 'Failed to load utility bills. Please try again later.',
            isLoading: false,
            isConnected: false,
          });
        }
      },

      getUtilityBillById: async (id) => {
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { data, error } = await supabaseClient
            .from('utility_bills')
            .select('*, utility_services(*, utility_charges(*))')
            .eq('id', id)
            .eq('user_id', userId)
            .single();

          if (error) throw error;

          return data;
        } catch (error) {
          console.error('Error fetching utility bill by ID:', error.message);
          return null;
        }
      },

      addUtilityBill: async (utilityBillId) => {
        try {
          const { data, error } = await supabaseClient
            .from('utility_bills')
            .select('*, utility_services(*, utility_charges(*))')
            .eq('id', utilityBillId)
            .single();

          if (error) throw error;

          set((state) => ({
            utilityBills: [data, ...state.utilityBills],
          }));
          get().applyFiltersAndSort();
        } catch (error) {
          console.error('Error adding new utility bill:', error.message);
        }
      },

      updateUtilityBill: async (utilityBillId) => {
        try {
          const { data, error } = await supabaseClient
            .from('utility_bills')
            .select('*, utility_services(*, utility_charges(*))')
            .eq('id', utilityBillId)
            .single();

          if (error) throw error;

          set((state) => ({
            utilityBills: state.utilityBills.map((bill) =>
              bill.id === utilityBillId ? data : bill
            ),
          }));
          get().applyFiltersAndSort();
        } catch (error) {
          console.error('Error updating utility bill:', error.message);
        }
      },

      deleteUtilityBill: (utilityBillId) => {
        set((state) => ({
          utilityBills: state.utilityBills.filter((bill) => bill.id !== utilityBillId),
        }));
        get().applyFiltersAndSort();
      },

      setFilters: (newFilters) => {
        set((state) => ({
          filters: { ...state.filters, ...newFilters },
        }));
        get().applyFiltersAndSort();
      },

      setSortBy: (sortBy) => {
        set({ sortBy });
        get().applyFiltersAndSort();
      },

      applyFiltersAndSort: () => {
        const { utilityBills, filters, sortBy } = get();
        let filtered = [...utilityBills];

        // Apply filters
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          filtered = filtered.filter(
            (bill) =>
              bill.contract_number.toLowerCase().includes(searchLower) ||
              bill.bill_number.toLowerCase().includes(searchLower) ||
              bill.customer_name.toLowerCase().includes(searchLower) ||
              bill.customer_address.toLowerCase().includes(searchLower)
          );
        }

        if (filters.dateFrom) {
          filtered = filtered.filter(
            (bill) => new Date(bill.due_date) >= new Date(filters.dateFrom)
          );
        }

        if (filters.dateTo) {
          filtered = filtered.filter(
            (bill) => new Date(bill.due_date) <= new Date(filters.dateTo)
          );
        }

        if (filters.amountMin !== null && filters.amountMin !== '') {
          filtered = filtered.filter(
            (bill) => parseFloat(bill.total_amount) >= parseFloat(filters.amountMin)
          );
        }

        if (filters.amountMax !== null && filters.amountMax !== '') {
          filtered = filtered.filter(
            (bill) => parseFloat(bill.total_amount) <= parseFloat(filters.amountMax)
          );
        }

        if (filters.status && filters.status !== 'all') {
          filtered = filtered.filter((bill) => bill.status === filters.status);
        }

        // Apply sorting
        const [sortField, sortDirection] = sortBy.split('-');
        filtered.sort((a, b) => {
          let comparison = 0;
          
          if (sortField === 'due_date') {
            comparison = new Date(a.due_date) - new Date(b.due_date);
          } else if (sortField === 'amount') {
            comparison = parseFloat(a.total_amount) - parseFloat(b.total_amount);
          } else if (sortField === 'contract') {
            comparison = a.contract_number.localeCompare(b.contract_number);
          } else if (sortField === 'customer') {
            comparison = a.customer_name.localeCompare(b.customer_name);
          }
          
          return sortDirection === 'asc' ? comparison : -comparison;
        });

        set({ filteredUtilityBills: filtered });
      },

      createUtilityBill: async (billData, servicesData) => {
        set({ isLoading: true, error: null });
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          // Add user_id to bill data
          const billDataWithUser = { ...billData, user_id: userId };
          
          const storedUserStr = localStorage.getItem('invoice_user');
          if (storedUserStr) {
            const storedUser = JSON.parse(storedUserStr);
            if (storedUser.is_sub_user) {
              billDataWithUser.created_by_sub_user = storedUser.sub_user_id;
            }
          }
          
          // Insert the utility bill
          const { data: billResult, error: billError } = await supabaseClient
            .from('utility_bills')
            .insert([billDataWithUser])
            .select()
            .single();

          if (billError) throw billError;

          // Insert services and their charges
          for (const service of servicesData) {
            const serviceData = {
              utility_bill_id: billResult.id,
              service_type: service.service_type,
              total_amount: service.total_amount,
              user_id: userId
            };

            const { data: serviceResult, error: serviceError } = await supabaseClient
              .from('utility_services')
              .insert([serviceData])
              .select()
              .single();

            if (serviceError) throw serviceError;

            // Insert charges for this service
            if (service.charges && service.charges.length > 0) {
              const chargesData = service.charges.map(charge => ({
                utility_service_id: serviceResult.id,
                concept_name: charge.concept_name,
                quantity: charge.quantity,
                unit_value: charge.unit_value,
                subtotal: charge.subtotal,
                user_id: userId
              }));

              const { error: chargesError } = await supabaseClient
                .from('utility_charges')
                .insert(chargesData);

              if (chargesError) throw chargesError;
            }
          }

          set({ isLoading: false });
          return { success: true, id: billResult.id };
        } catch (error) {
          console.error('Error creating utility bill:', error.message);
          set({
            error: 'Failed to create utility bill. Please try again later.',
            isLoading: false,
          });
          return { success: false, error: error.message };
        }
      },

      updateUtilityBillStatus: async (id, status) => {
        try {
          const userId = getUserId();
          if (!userId) {
            throw new Error('User not authenticated');
          }

          const { error } = await supabaseClient
            .from('utility_bills')
            .update({ status })
            .eq('id', id)
            .eq('user_id', userId);

          if (error) throw error;

          set((state) => ({
            utilityBills: state.utilityBills.map((bill) =>
              bill.id === id ? { ...bill, status } : bill
            ),
          }));
          get().applyFiltersAndSort();
          return true;
        } catch (error) {
          console.error('Error updating utility bill status:', error.message);
          return false;
        }
      },
    }));

    return store;
  } catch (error) {
    console.error('Error creating utility bill store:', error);
    throw error;
  }
};

export default createUtilityBillStore;
